import {
  ExportPayload,
  ExportResponse,
  ExportStatusType,
} from 'api/async-export';
import { HttpClient } from 'api/http-client';
import type { PaginatedResponse } from 'api/types';
import {
  FieldCustomType,
  ReportingSourceType,
  TabularReportRequest,
  CustomReport,
  CustomReportPayload,
  PivotFields,
  PivotReportRequest,
  PivotFieldsDefinition,
  ResponseType,
  HighlightingRule,
  CustomReportExportRequest,
  NumberFieldsMap,
  SortOrderType,
} from 'components/pages/custom-reports/types';

const BASE_URL = '/custom-reports';

export interface PaginatedCustomReportsParams {
  search?: string;
  limit: number;
  offset: number;
  orderBy?: string;
  sortOrder?: SortOrderType;
  filter?: string;
  groupBy?: string;
}

export type PaginatedCustomReportsResponse = PaginatedResponse<CustomReport>;

class CustomReportsApiClient extends HttpClient {
  public getFields = async (
    sourceType: ReportingSourceType,
  ): Promise<FieldCustomType> =>
    this.get(`${BASE_URL}/get-fields?source=${sourceType}`);

  public getNumberFields = async (
    sourceType: ReportingSourceType,
  ): Promise<NumberFieldsMap> =>
    this.get(`${BASE_URL}/formula-builder-fields?source=${sourceType}`);

  public getPivotFields = async (
    sourceType: ReportingSourceType,
  ): Promise<PivotFields> =>
    this.get(`${BASE_URL}/get-pivot-fields?source=${sourceType}`);

  public getPivotDefinition = async (
    pivotReportRequest: PivotReportRequest,
  ): Promise<PivotFieldsDefinition[]> =>
    this.post(`${BASE_URL}/get-pivot-definition`, pivotReportRequest);

  public getPivotData = async (
    pivotReportRequest: PivotReportRequest,
  ): Promise<ResponseType> =>
    this.post(`${BASE_URL}/get-pivot-data`, pivotReportRequest);

  public getTabularGroups = async (
    tabularReportRequest: TabularReportRequest,
  ): Promise<ResponseType> =>
    this.post(`${BASE_URL}/get-tabular-groups`, tabularReportRequest);

  public getTabularData = async (
    tabularReportRequest: TabularReportRequest,
  ): Promise<ResponseType> =>
    this.post(`${BASE_URL}/get-tabular-data`, tabularReportRequest);

  public getRowTotalReport = async (
    customReportPayload: TabularReportRequest,
  ): Promise<ResponseType> =>
    this.post(`${BASE_URL}/get-row-count`, customReportPayload);

  public getPivotColumnTotal = async (
    customReportPayload: PivotReportRequest,
  ): Promise<ResponseType> =>
    this.post(`${BASE_URL}/pivot-column-total`, customReportPayload);

  public getAllCustomReports = async (): Promise<CustomReport[]> =>
    this.get(`${BASE_URL}/get-all-custom-reports`);

  public getPaginatedCustomReports = async (
    params: PaginatedCustomReportsParams,
  ): Promise<PaginatedCustomReportsResponse> => {
    const searchParams = new URLSearchParams();

    if (params.search != null && params.search !== '') {
      searchParams.set('search', params.search);
    }
    searchParams.set('limit', String(params.limit));
    searchParams.set('offset', String(params.offset));
    if (
      params.orderBy != null &&
      params.orderBy !== '' &&
      params.sortOrder != null
    ) {
      searchParams.set('sort', `${params.orderBy}.${params.sortOrder}`);
    }

    const query = searchParams.toString();

    return this.get<PaginatedCustomReportsResponse>(
      `${BASE_URL}/paginated${query ? `?${query}` : ''}`,
    );
  };

  public getCustomReport = async (
    customReportId: number,
  ): Promise<CustomReport> =>
    this.get(`${BASE_URL}/get-custom-report/${customReportId}`);

  public createCustomReport = async (
    customReportPayload: CustomReportPayload,
  ): Promise<CustomReport> =>
    this.post(`${BASE_URL}/create-custom-reports`, customReportPayload);

  public saveCustomReport = async (
    customReportPayload: CustomReport,
  ): Promise<CustomReport> =>
    this.post(`${BASE_URL}/save`, customReportPayload);

  public deleteCustomReport = async (widgetId: number): Promise<void> =>
    this.delete(`${BASE_URL}/delete/${widgetId}`);

  public getConditionalHighlight = async (
    customReportPayload: CustomReportPayload,
  ): Promise<HighlightingRule[]> =>
    this.post(`${BASE_URL}/conditional-highlighting`, customReportPayload);

  public getExportData = async (
    request: CustomReportExportRequest,
  ): Promise<ExportResponse> => {
    const exportPayload = {
      statusType: ExportStatusType.CustomReportExport,
      statusMeta: { request },
    };

    return this.post<ExportResponse, ExportPayload>(
      'async/status',
      exportPayload,
    );
  };

  // todo remove these methods later
  public getTabularExportData = async (
    exportRequest: TabularReportRequest,
  ): Promise<ResponseType> =>
    this.post(`${BASE_URL}/tabular/export`, exportRequest);

  public getPivotExportData = async (
    exportRequest: TabularReportRequest,
  ): Promise<ResponseType> =>
    this.post(`${BASE_URL}/pivot/export`, exportRequest);

  public getFormulaFieldsFeatureFlag() {
    return this.get<{
      enabled: boolean;
    }>(`${BASE_URL}/enable`);
  }
}

export const CustomReportsApi = new CustomReportsApiClient();
